import {
  Component,
  OnInit,
  OnDestroy,
  EventEmitter,
  Output,
  Input,
  ChangeDetectorRef,
  ViewChild,
  HostListener,
  ElementRef,
} from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { Observable, Subject, of, BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil, tap, catchError } from 'rxjs/operators';
import { ProceduresCombosService } from '../procedures-combos.service';
import { NotificationsService } from '@lib/notifications';
import {
  ProcedureItem,
  ProcedureProtocol,
  SelectedProcedureProtocol,
} from '../../../../project/modules/session-details-shared/session-details-shared-medical-record/interfaces/procedures';

@Component({
  selector: 'app-procedures-select',
  templateUrl: './procedures-select.component.html',
  styleUrls: ['./procedures-select.component.scss'],
})
export class ProceduresSelectComponent implements OnInit, OnDestroy {
  @Input() public disabled = false;
  @Input() public reset$: Observable<void>;
  @Input() public proceduresArray: FormArray;

  @Output() public onProtocolSelectionChanged = new EventEmitter<{
    selectedProtocols: SelectedProcedureProtocol[];
    procedureType: string;
  }>();

  @ViewChild('panel') public panelRef: ElementRef;
  @ViewChild('trigger') public triggerRef: ElementRef;

  public protocols: ProcedureProtocol[] = [];
  public selectedProtocolObjects: SelectedProcedureProtocol[] = [];
  public isPanelOpen = false;
  public isLoading = false;
  public isLastPage = false;

  public searchCtrl = new FormControl('');
  public procedureTypeCtrl = new FormControl('APAC');

  private readonly confirmedProcedureTypeSubject = new BehaviorSubject<string>('APAC');
  public procedureTypeConfirmed$ = this.confirmedProcedureTypeSubject.asObservable();

  private expandedProtocolsIds = new Set<string>();
  private offset = 0;
  private readonly limit = 50;
  private readonly destroy$ = new Subject<void>();

  constructor(private proceduresService: ProceduresCombosService, private cdr: ChangeDetectorRef) {}

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    const isOutsideClick =
      !this.panelRef?.nativeElement.contains(event.target) && !this.triggerRef?.nativeElement.contains(event.target);

    if (isOutsideClick) {
      this.isPanelOpen = false;
    }
  }

  public ngOnInit(): void {
    this.initializeStreams();
    this.initializeResetListener();
    this.loadProtocols();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeStreams(): void {
    this.procedureTypeCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((type) => this.handleTypeChange(type));

    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => this.resetPagination()),
        switchMap((search) => this.fetchProtocols(search)),
        takeUntil(this.destroy$),
      )
      .subscribe((data) => this.updateProtocolsList(data));
  }

  private initializeResetListener(): void {
    this.reset$?.pipe(takeUntil(this.destroy$)).subscribe(() => this.resetComponent());
  }

  private handleTypeChange(newType: string): void {
    const currentType = this.confirmedProcedureTypeSubject.value;

    if (newType === currentType) {
      return;
    }

    this.confirmedProcedureTypeSubject.next(newType);
    this.resetPagination();
    this.loadProtocols();
  }

  private fetchProtocols(search: string): Observable<ProcedureProtocol[]> {
    this.isLoading = true;
    const query = {
      type: this.confirmedProcedureTypeSubject.value,
      name: search || undefined,
      limit: this.limit,
      offset: this.offset,
    };

    return this.proceduresService.getProcedureCombos(query).pipe(
      catchError(() => {
        this.isLoading = false;
        return of([]);
      }),
    );
  }

  public loadProtocols(isAppend = false): void {
    if (this.isLoading && isAppend) return;

    this.fetchProtocols(this.searchCtrl.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => this.updateProtocolsList(data, isAppend));
  }

  private updateProtocolsList(data: ProcedureProtocol[], isAppend = false): void {
    const results = Array.isArray(data) ? data : [];
    this.isLastPage = results.length < this.limit;
    this.protocols = isAppend ? [...this.protocols, ...results] : results;
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  public onPanelScroll(event: any): void {
    const { scrollHeight, scrollTop, clientHeight } = event.target;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 10;

    if (isNearBottom && !this.isLoading && !this.isLastPage) {
      this.offset += this.limit;
      this.loadProtocols(true);
    }
  }

  public toggleProtocolSelection(protocol: ProcedureProtocol): void {
    const isSelected = this.isProtocolSelected(protocol.id);

    if (isSelected) {
      this.removeProtocol(protocol.id);
    } else {
      this.addProtocol(protocol);
    }

    this.emitSelection();
  }

  public toggleProcedureSelection(procedure: any, protocol: ProcedureProtocol): void {
    const isSelected = this.isProcedureSelected(procedure.id);

    if (isSelected) {
      this.handleRemove({ procedureId: procedure.id, protocolId: protocol.id });
      return;
    }

    this.addSingleProcedure(procedure, protocol);
    this.emitSelection();
  }

  private addProtocol(protocol: ProcedureProtocol): void {
    const alreadySelected = this.isProtocolSelected(protocol.id);
    if (alreadySelected) return;

    const newProtocol: SelectedProcedureProtocol = {
      ...protocol,
      type: this.confirmedProcedureTypeSubject.value as 'APAC' | 'SADT',
      procedures: [...(protocol.procedures || [])],
    };

    this.selectedProtocolObjects = [...this.selectedProtocolObjects, newProtocol];
  }

  private removeProtocol(id: string): void {
    this.selectedProtocolObjects = this.selectedProtocolObjects.filter((protocol) => protocol.id !== id);
  }

  private addSingleProcedure(procedure: ProcedureItem, protocol: ProcedureProtocol): void {
    const protocolIndex = this.selectedProtocolObjects.findIndex((p) => p.id === protocol.id);

    if (protocolIndex === -1) {
      const newProtocol: SelectedProcedureProtocol = {
        ...protocol,
        type: this.confirmedProcedureTypeSubject.value as 'APAC' | 'SADT',
        procedures: [procedure],
      };

      this.selectedProtocolObjects = [...this.selectedProtocolObjects, newProtocol];
    } else {
      this.selectedProtocolObjects = this.selectedProtocolObjects.map((p, index) =>
        index === protocolIndex ? { ...p, procedures: [...(p.procedures || []), procedure] } : p,
      );
    }
  }

  public handleRemove(event: { procedureId: string; protocolId: string }): void {
    this.selectedProtocolObjects = this.selectedProtocolObjects
      .map((protocol) => {
        if (protocol.id !== event.protocolId) return protocol;
        return {
          ...protocol,
          procedures: protocol.procedures.filter((p) => p.id !== event.procedureId),
        };
      })
      .filter((protocol) => protocol.procedures.length > 0);

    this.emitSelection();
  }

  public isProtocolSelected(id: string): boolean {
    return this.selectedProtocolObjects.some((protocol) => protocol.id === id);
  }

  public isProcedureSelected(id: string): boolean {
    return this.selectedProtocolObjects.some((protocol) => protocol.procedures.some((p) => p.id === id));
  }

  public toggleExpand(id: string): void {
    this.expandedProtocolsIds.has(id) ? this.expandedProtocolsIds.delete(id) : this.expandedProtocolsIds.add(id);
  }

  public isExpanded(id: string): boolean {
    return this.expandedProtocolsIds.has(id);
  }

  public togglePanel(): void {
    if (!this.disabled) this.isPanelOpen = !this.isPanelOpen;
  }

  private emitSelection(): void {
    this.onProtocolSelectionChanged.emit({
      selectedProtocols: this.selectedProtocolObjects,
      procedureType: this.confirmedProcedureTypeSubject.value,
    });
    this.cdr.markForCheck();
  }

  private resetPagination(): void {
    this.offset = 0;
    this.isLastPage = false;
    this.protocols = [];
    this.cdr.markForCheck();
  }

  private resetComponent(): void {
    this.confirmedProcedureTypeSubject.next('APAC');
    this.procedureTypeCtrl.setValue('APAC', { emitEvent: false });
    this.searchCtrl.setValue('', { emitEvent: false });
    this.selectedProtocolObjects = [];
    this.expandedProtocolsIds.clear();
    this.resetPagination();
    this.loadProtocols();
  }
}
